import { useEffect, useMemo, useState } from "react";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { pushNotificationsApi } from "@services/api/pushNotifications";
import { useDataStore } from "@store/user_org";
import { useProjectStore } from "@store/projectStore";
import BaseModal from "@components/modals/BaseModal";
import Icon1 from "@assets/icons/Icon";
import { accessibleProjectIdsFor } from "@helpers/accessHelpers";

type SentNotif = {
  id: string;
  title: string;
  body: string;
  data: Record<string, string>;
  user_ids: string[];
  team_user_ids: string[];
  category?: string;
  org_id?: string;
  project_id?: string;
  sender_user_id?: string;
  token_count: number;
  success_count: number;
  failure_count: number;
  sent_at: string;
  opened_by: string[];
  opened_at: Record<string, string>;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  if (sameDay) {
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  }
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function openRatePercent(opened: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((opened / total) * 100);
}

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

function NotifDetailModal({
  notif,
  onClose,
}: {
  notif: SentNotif;
  onClose: () => void;
}) {
  const orgMembers = useDataStore((s) => s.orgMembers);
  const projects = useProjectStore((s) => s.projects);
  const nameFor = (uid: string) => {
    const m = orgMembers.find((u) => u.id === uid);
    return m?.infos?.display_name || m?.infos?.full_name || m?.infos?.email || uid;
  };
  const projectName = notif.project_id
    ? projects.find((p) => p.id === notif.project_id)?.infos?.general?.name
    : undefined;

  const openedSet = new Set(notif.opened_by);
  const rate = openRatePercent(notif.opened_by.length, notif.user_ids.length);

  return (
    <BaseModal onClose={onClose}>
      <div className="flex flex-col gap-4 p-6 max-w-700 w-full">
        <div className="flex flex-col gap-1">
          <div className="text-h2 font-semibold text-neutral-800">{notif.title}</div>
          <div className="text-h3 text-neutral-600 whitespace-pre-wrap">{notif.body}</div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-h4">
          <div className="flex flex-col gap-0.5">
            <div className="text-neutral-400">Envoyée le</div>
            <div className="text-neutral-700">{formatDate(notif.sent_at)}</div>
          </div>
          {notif.category && (
            <div className="flex flex-col gap-0.5">
              <div className="text-neutral-400">Catégorie</div>
              <div className="text-neutral-700">{notif.category}</div>
            </div>
          )}
          {notif.project_id && (
            <div className="flex flex-col gap-0.5">
              <div className="text-neutral-400">Projet</div>
              <div className="text-neutral-700">
                {projectName || notif.project_id}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-0.5">
            <div className="text-neutral-400">Appareils ciblés</div>
            <div className="text-neutral-700">
              {notif.token_count} ({notif.success_count} envoyés, {notif.failure_count} échecs)
            </div>
          </div>
          <div className="flex flex-col gap-0.5">
            <div className="text-neutral-400">Taux d'ouverture</div>
            <div className="text-neutral-700">
              {notif.opened_by.length} / {notif.user_ids.length}
              {notif.user_ids.length > 0 && <span className="text-neutral-400"> ({rate}%)</span>}
            </div>
          </div>
          {notif.sender_user_id && (
            <div className="flex flex-col gap-0.5">
              <div className="text-neutral-400">Envoyée par</div>
              <div className="text-neutral-700">{nameFor(notif.sender_user_id)}</div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-h4 text-neutral-400 font-medium">Destinataires</div>
          <div className="flex flex-col divide-y divide-neutral-100 max-h-300 overflow-auto">
            {notif.user_ids.length === 0 && (
              <div className="text-h4 text-neutral-400 py-2">
                Aucun destinataire enregistré.
              </div>
            )}
            {notif.user_ids.map((uid) => {
              const opened = openedSet.has(uid);
              const openedAt = notif.opened_at?.[uid];
              return (
                <div
                  key={uid}
                  className="flex flex-row items-center justify-between py-2 gap-3"
                >
                  <div className="text-h4 text-neutral-700 truncate">{nameFor(uid)}</div>
                  {opened ? (
                    <div className="flex items-center gap-1.5 text-h5 text-emerald-600 flex-shrink-0">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Ouverte {openedAt ? `· ${formatDate(openedAt)}` : ""}
                    </div>
                  ) : (
                    <div className="text-h5 text-neutral-400 flex-shrink-0">Non ouverte</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {Object.keys(notif.data || {}).length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="text-h4 text-neutral-400 font-medium">Données</div>
            <pre className="text-h5 bg-neutral-50 rounded-lg p-3 overflow-auto">
              {JSON.stringify(notif.data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </BaseModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const PushNotificationsSent = ({
  embedded = false,
}: {
  /** Hide the outer page header when rendered inside another page. */
  embedded?: boolean;
} = {}) => {
  const projects = useProjectStore((s) => s.projects);
  const userData = useDataStore((s) => s.userData);
  const [rawNotifs, setRawNotifs] = useState<SentNotif[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [selected, setSelected] = useState<SentNotif | null>(null);

  // Belt-and-braces frontend filter on top of the server-side scoping at
  // /push/sent. Non-admins only see notifications tied to a project they
  // can access — system-wide notifications without a project are dropped.
  const accessibleProjectIds = useMemo(
    () => accessibleProjectIdsFor(userData, projects),
    [userData, projects],
  );
  const notifs = useMemo(() => {
    if (accessibleProjectIds === "all") return rawNotifs;
    return rawNotifs.filter(
      (n) => n.project_id && accessibleProjectIds.has(n.project_id),
    );
  }, [rawNotifs, accessibleProjectIds]);

  const fetch = async () => {
    setIsLoading(true);
    try {
      const data = await pushNotificationsApi.listSent({
        category: categoryFilter || undefined,
        limit: 100,
      });
      setRawNotifs(data.notifications || []);
    } catch {
      setRawNotifs([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    notifs.forEach((n) => n.category && set.add(n.category));
    return Array.from(set).sort();
  }, [notifs]);

  // Aggregate stats for the header
  const stats = useMemo(() => {
    const total = notifs.length;
    let totalRecipients = 0;
    let totalOpens = 0;
    for (const n of notifs) {
      totalRecipients += n.user_ids.length;
      totalOpens += n.opened_by.length;
    }
    return {
      total,
      rate: openRatePercent(totalOpens, totalRecipients),
      recipients: totalRecipients,
      opens: totalOpens,
    };
  }, [notifs]);

  return (
    <div className={`flex flex-col ${embedded ? "" : "h-fit pb-96 mt-10 mx-4 lg:ml-10"}`}>
      <div className="flex flex-col w-full max-w-900 gap-4">
        {!embedded && (
          <div className="flex flex-col">
            <Text1>Notifications envoyées</Text1>
            <Text2>
              Historique des notifications push envoyées depuis Hexaflow, avec le
              taux d'ouverture côté client mobile.
            </Text2>
          </div>
        )}

        {/* Stats cards */}
        <div className="flex flex-row gap-3">
          <div className="flex-1 bg-white rounded-lg border px-4 py-3">
            <div className="text-h4 text-neutral-400">Envoyées</div>
            <div className="text-h1 font-semibold text-neutral-800">{stats.total}</div>
          </div>
          <div className="flex-1 bg-white rounded-lg border px-4 py-3">
            <div className="text-h4 text-neutral-400">Destinataires</div>
            <div className="text-h1 font-semibold text-neutral-800">{stats.recipients}</div>
          </div>
          <div className="flex-1 bg-white rounded-lg border px-4 py-3">
            <div className="text-h4 text-neutral-400">Ouvertures</div>
            <div className="text-h1 font-semibold text-neutral-800">{stats.opens}</div>
          </div>
          <div className="flex-1 bg-white rounded-lg border px-4 py-3">
            <div className="text-h4 text-neutral-400">Taux d'ouverture</div>
            <div className="text-h1 font-semibold text-emerald-600">{stats.rate}%</div>
          </div>
        </div>

        {/* Category filter */}
        {categories.length > 0 && (
          <div className="flex flex-row items-center gap-2 flex-wrap">
            <button
              onClick={() => setCategoryFilter("")}
              className={`px-3 py-1 rounded-full text-h5 border transition-colors ${
                categoryFilter === ""
                  ? "bg-neutral-800 text-white border-neutral-800"
                  : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
              }`}
            >
              Toutes
            </button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategoryFilter(c)}
                className={`px-3 py-1 rounded-full text-h5 border transition-colors ${
                  categoryFilter === c
                    ? "bg-neutral-800 text-white border-neutral-800"
                    : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {/* List */}
        <div className="flex flex-col bg-white rounded-lg border overflow-hidden">
          {isLoading && (
            <div className="text-h3 text-neutral-400 py-10 text-center">
              Chargement…
            </div>
          )}
          {!isLoading && notifs.length === 0 && (
            <div className="text-h3 text-neutral-400 py-10 text-center">
              Aucune notification envoyée pour le moment.
            </div>
          )}
          {!isLoading &&
            notifs.map((n) => {
              const rate = openRatePercent(n.opened_by.length, n.user_ids.length);
              const hasRecipients = n.user_ids.length > 0;
              const projectName = n.project_id
                ? projects.find((p) => p.id === n.project_id)?.infos?.general?.name
                : undefined;
              return (
                <div
                  key={n.id}
                  onClick={() => setSelected(n)}
                  className="flex flex-row items-center gap-3 px-4 py-3 border-b border-neutral-100 last:border-b-0 cursor-pointer hover:bg-neutral-50"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <Icon1 name="sms" size={16} color="#3b82f6" />
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <div className="flex flex-row items-baseline gap-2 min-w-0">
                      <span className="text-h3 font-medium text-neutral-800 truncate">
                        {n.title}
                      </span>
                      {n.category && (
                        <span className="text-h5 text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded flex-shrink-0">
                          {n.category}
                        </span>
                      )}
                      {projectName && (
                        <span className="text-h5 text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex-shrink-0 max-w-40 truncate">
                          {projectName}
                        </span>
                      )}
                    </div>
                    <div className="text-h4 text-neutral-400 truncate">{n.body}</div>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
                    <div className="text-h5 text-neutral-400">{formatDate(n.sent_at)}</div>
                    {hasRecipients ? (
                      <div
                        className={`text-h5 font-medium ${
                          rate >= 50 ? "text-emerald-600" : rate > 0 ? "text-amber-600" : "text-neutral-400"
                        }`}
                      >
                        {n.opened_by.length}/{n.user_ids.length} ouv. ({rate}%)
                      </div>
                    ) : (
                      <div className="text-h5 text-neutral-400">
                        {n.token_count} appareil{n.token_count > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {selected && (
        <NotifDetailModal notif={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
};
